import React from 'react';
import { StatusPill, StatusTone } from '../../../status-pill/status-pill';
import { listItemMotion } from '../../../../motion';
import {
  PASS_CATEGORY_LABELS,
  PassOccurrence,
  ResidentPass,
  ResidentPassState,
} from '../../../../interfaces/visitor-pass';
import {
  calendarDayOf,
  describeSchedule,
  describeWindow,
  formatPassDate,
  formatVisitDay,
} from '../../../../utils/pass-schedule';
import {
  PassLine,
  PassLink,
  PassMain,
  PassName,
  PassPills,
  PassRow,
} from './portal-visitors.styled';

/**
 * ONE PASS ON THE VISITORS TAB (resident-portal Plan 3, RP-3.4).
 *
 * Read only: the whole row opens the pass's own page. What a resident needs to
 * recognise it at a glance — who, what kind of visitor, the schedule in one
 * sentence, and when they are next due — and whether the building added it,
 * which is the sentence that makes a pass the resident did not ask for
 * noticeable.
 */

/** The derived state as a word and a tone (UI-CONTRACT §4: active → success,
 *  paused / scheduled / pending → warning, ended / off → neutral). */
export const PASS_STATE_PILL: Record<ResidentPassState, { label: string; tone: StatusTone }> = {
  active: { label: 'Active', tone: 'success' },
  paused: { label: 'Paused', tone: 'warning' },
  scheduled: { label: 'Starts later', tone: 'warning' },
  ended: { label: 'Ended', tone: 'neutral' },
  off: { label: 'Switched off', tone: 'neutral' },
  pending: { label: 'Waiting for approval', tone: 'warning' },
};

/** The building's calendar date ('YYYY-MM-DD'), for "Tue 30 Sep" without a
 *  year and for "starts later". en-CA's short date IS that shape; a zone Intl
 *  rejects falls back to the reader's own day, as the portal's clocks do. */
export const buildingToday = (zone?: string | null): string => calendarDayOf(zone);

export const visitorName =(pass: Pick<ResidentPass, 'first_name' | 'last_name'>): string =>
  [pass.first_name, pass.last_name].map(part => (part || '').trim()).filter(Boolean).join(' ') ||
  'A visitor';

/** "Tue 30 Sep, 09:00–13:00" / "Tue 30 Sep, any time" — the building's own
 *  wall clock: the occurrence's date is already the building's local date and
 *  the window is the pass's local window. */
export const visitWhen = (
  occurrence: PassOccurrence,
  pass: Pick<ResidentPass, 'window_start' | 'window_end'>,
  today?: string | null
): string =>
  `${formatVisitDay(occurrence.local_date, today)}, ${
    occurrence.all_day ? 'any time' : describeWindow(pass.window_start, pass.window_end)
  }`;

export const PASS_PAUSED_LINE = 'Paused';
export const PASS_NO_MORE_VISITS_LINE = 'No more visits on this pass';

/**
 * The card's second line: what happens next on this pass, or why nothing does.
 *
 *   Next: Tue 30 Sep, 09:00–13:00    a visit is coming
 *   Starts 4 Dec 2026                not begun, first visit beyond the list
 *   Paused / Paused until 12 Oct     paused, nothing coming meanwhile
 *   No more visits on this pass      running, but nothing is left on it
 *   ''                               ended, or the building has passes off
 *
 * "Starts" is said ONLY of a pass that has not begun. It used to be the answer
 * whenever nothing was coming, so a pass paused in its second month read
 * "Starts 1 Sep" (QA Q-14); the state is checked here as well as on the server,
 * so an older server cannot bring that back.
 */
export const nextLine = (pass: ResidentPass, today?: string | null): string => {
  if (pass.state === 'ended' || pass.state === 'off') return '';
  if (pass.state === 'pending') return 'Nobody is expected until your building approves it.';
  const next = pass.next && pass.next[0];
  if (next) return `Next: ${visitWhen(next, pass, today)}`;
  if (pass.state === 'scheduled') {
    return pass.starts_on ? `Starts ${formatPassDate(pass.starts_on)}` : '';
  }
  if (pass.state === 'paused') {
    const until = pass.paused_until;

    return until && (!today || until >= today)
      ? `${PASS_PAUSED_LINE} until ${formatPassDate(until)}`
      : PASS_PAUSED_LINE;
  }

  return PASS_NO_MORE_VISITS_LINE;
};

export const scheduleLine = (pass: ResidentPass, today?: string | null): string =>
  describeSchedule(pass, { today });

interface PassCardProps {
  pass: ResidentPass;
  /** The building's calendar date, for "next" and the schedule sentence. */
  today?: string | null;
  /** Index within its page, for the capped cascade. */
  index: number;
}

export const PassCard: React.FC<PassCardProps> = ({ pass, today, index }) => {
  const state = PASS_STATE_PILL[pass.state] || PASS_STATE_PILL.active;
  const next = nextLine(pass, today);
  const name = visitorName(pass);

  return (
    <PassRow {...listItemMotion(index)} layout={false} data-testid="pass-card">
      <PassLink to={`/portal/visitors/${pass.pass_id}`}>
        <PassMain>
          <PassName>{name}</PassName>
          <PassLine>{scheduleLine(pass, today)}</PassLine>
          {next ? <PassLine>{next}</PassLine> : null}
          {pass.created_via === 'staff' ? <PassLine>Added by your building</PassLine> : null}
          {pass.decision === 'rejected' ? <PassLine>Declined by your building</PassLine> : null}
        </PassMain>
        <PassPills>
          <StatusPill tone="neutral">{PASS_CATEGORY_LABELS[pass.category] || 'Visitor'}</StatusPill>
          <StatusPill tone={state.tone} dot>
            {state.label}
          </StatusPill>
        </PassPills>
      </PassLink>
    </PassRow>
  );
};
